import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import { API_URL, authHeaders, nf, quartiere, formatData, statoBadgeClass, Stella, AvvisoEsecuzioni, clicFuori } from "./comuni";

/**
 * Modale "Dettaglio" di una tratta (task 483): lo storico dei percorsi 122 in cui
 * compare, in ordine cronologico, con esito (fatta / non fatta / in attesa) e la
 * stella che la tratta aveva DOPO ogni passaggio. E' il "perche'" della stella
 * gialla o rossa che si vede nei tab.
 *
 * Condiviso da "Tratte da recuperare" e "Anagrafica Tratti": stesso endpoint
 * (GET /gestione-percorsi/tratti/:id/storico), stessa tabella. `intestazione`
 * serve a riempire l'header mentre i dati arrivano.
 *
 * Markup ricalcato sul modale dettaglio percorso di TabPercorsi. L'overlay ha
 * `gp-overlay-top` perche' il modale si apre anche dalla tabella a tutto schermo
 * (z-index 9999): senza, finirebbe sotto. Qui dentro non ci sono select ne'
 * loader a overlay, quindi alzare lo z-index non copre nient'altro.
 */

const ESITO = {
    fatta: { label: "Fatto", cls: "badge-stato-completate" },
    non_fatta: { label: "Non fatto", cls: "badge-stato-noneseguite" },
    in_attesa: { label: "In attesa", cls: "badge-stato-lavorazione" },
};

const ModaleStoricoTratta = ({ trattoId, intestazione = {}, onClose }) => {
    const [dati, setDati] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);
    // Ordine della colonna Data: il backend manda le righe dal piu' vecchio al piu' recente
    // ("asc"), ma si parte dal piu' RECENTE ("desc": e' quello che interessa, la situazione
    // attuale in cima); cliccando l'intestazione si inverte. Solo la vista cambia, non i conteggi.
    const [ordine, setOrdine] = useState("desc");

    const carica = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/tratti/${trattoId}/storico`, { headers: authHeaders() });
            setDati(res.data);
        } catch (err) {
            console.error("Errore storico tratta:", err);
            setErrore(err.response?.status === 404 ? "Tratta non trovata in anagrafica." : "Impossibile caricare lo storico della tratta.");
        } finally {
            setLoading(false);
        }
    }, [trattoId]);

    useEffect(() => { carica(); }, [carica]);

    // ESC chiude, come ci si aspetta da un modale di sola lettura.
    useEffect(() => {
        const onEsc = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    // Finche' non arrivano i dati, l'header usa quello che il tab sa gia' della tratta.
    const t = dati?.tratto || {};
    const via = t.strada || intestazione.via || "—";
    const tratto = t.tratto || intestazione.tratto || "";
    const codice = t.codice || intestazione.codice || "";
    const righe = dati?.righe || [];
    const righeTotali = dati?.meta?.righeTotali ?? righe.length;
    const attuale = dati?.attuale || null;
    const soglia = dati?.sogliaRossa || 2;
    // L'ultima riga cronologica e' la situazione attuale: si evidenzia per id, cosi' resta
    // quella giusta anche con l'ordine invertito (dove finisce in cima).
    const idAttuale = righe.length ? righe[righe.length - 1].id : null;
    const righeVisibili = ordine === "asc" ? righe : [...righe].reverse();

    return (
        <div className="modal-overlay gp-overlay-top" {...clicFuori(onClose)}>
            <div className="modal" style={{ maxWidth: 900 }}>
                <div className="modal-header">
                    <div className="m-icon">
                        {/* orologio con freccia: "storico" */}
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><polyline points="12 7 12 12 15 14" />
                        </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h3>{via}</h3>
                        <div className="h-sub">{[tratto, codice].filter(Boolean).join(" · ")}</div>
                    </div>
                    <button className="modal-close" onClick={onClose} aria-label="Chiudi">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="gp-loading"><Lf23Loader testo="Caricamento storico..." /></div>
                    ) : errore ? (
                        <div className="gp-box-empty">
                            {errore}
                            <div style={{ marginTop: 10 }}><button className="btn btn-ghost" onClick={carica}>Riprova</button></div>
                        </div>
                    ) : (
                        <>
                            <div className="gp-detail-grid">
                                <div>
                                    <span className="gp-detail-label">Quartiere</span>
                                    <span className="gp-detail-val">{quartiere(t.quartiere || intestazione.quartiere)}</span>
                                </div>
                                <div>
                                    <span className="gp-detail-label">Passaggi / anno · Lunghezza</span>
                                    <span className="gp-detail-val">
                                        {t.passAnno != null ? t.passAnno : "—"} · {t.lunghezzaMetri != null ? `${nf(t.lunghezzaMetri)} m` : "—"}
                                    </span>
                                </div>
                                <div>
                                    <span className="gp-detail-label">Stella attuale</span>
                                    <span className="gp-detail-val gp-storico-attuale">
                                        {attuale?.livello ? (
                                            <>
                                                <Stella livello={attuale.livello} volte={attuale.consecutive} />
                                                <span>
                                                    {attuale.livello === "rossa" ? "Rossa" : "Gialla"} — non fatta {attuale.consecutive} volt{attuale.consecutive === 1 ? "a" : "e"} di fila
                                                </span>
                                            </>
                                        ) : (
                                            <span className="muted">Nessuna: eseguita all'ultimo passaggio concluso</span>
                                        )}
                                    </span>
                                </div>
                            </div>

                            <AvvisoEsecuzioni meta={dati?.meta}
                                testoDanno="In questo storico ogni passaggio concluso risulterebbe non fatto." />

                            {/* Il backend manda solo gli ultimi N passaggi (default 10), ma le stelle sono
                                calcolate su tutto lo storico: se c'e' stato un taglio lo si dice. */}
                            <div className="section-label">
                                Storico passaggi ({righeTotali > righe.length ? `ultimi ${righe.length} di ${righeTotali}` : righe.length})
                            </div>
                            {righe.length === 0 ? (
                                <div className="gp-box-empty">Questa tratta non compare in nessun percorso operativo.</div>
                            ) : (
                                <div className="table-container">
                                    <div className="table-scroll">
                                        <table className="gp-storico-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ cursor: "pointer" }} onClick={() => setOrdine((o) => (o === "asc" ? "desc" : "asc"))}
                                                        title={ordine === "asc" ? "Dal più vecchio al più recente: clicca per invertire" : "Dal più recente al più vecchio: clicca per invertire"}>
                                                        Data{ordine === "asc" ? " ▲" : " ▼"}
                                                    </th>
                                                    <th>Percorso</th>
                                                    <th>Stato percorso</th>
                                                    <th>Esito</th>
                                                    <th>Stella</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {righeVisibili.map((r) => {
                                                    const esito = ESITO[r.esito] || { label: r.esito, cls: "badge-neutral" };
                                                    return (
                                                        <tr key={r.id} className={r.id === idAttuale ? "gp-storico-ultima" : ""}>
                                                            <td className="gp-rep-nowrap">{formatData(r.data)}</td>
                                                            <td>
                                                                <span className="strong">{r.nome || "—"}</span>
                                                                {r.recupero && <span className="gp-percorso-pill" style={{ marginLeft: 6 }}>recupero</span>}
                                                                <div className="codice" style={{ fontSize: 11 }}>{r.customId || "—"}</div>
                                                            </td>
                                                            <td><span className={`badge ${statoBadgeClass(r.stato)}`}>{r.stato || "—"}</span></td>
                                                            <td>
                                                                <span className={`badge ${esito.cls}`}>{esito.label}</span>
                                                                {/* Stessa attribuzione della Reportistica: il passaggio saltato
                                                                    sa da quale giro e' stato recuperato, e viceversa. */}
                                                                {r.recuperataDa && (
                                                                    <div className="gp-storico-rec" title={r.recuperataDa.customId || undefined}>
                                                                        recuperata il {formatData(r.recuperataDa.data)}{r.recuperataDa.nome ? ` · ${r.recuperataDa.nome}` : ""}
                                                                    </div>
                                                                )}
                                                                {r.recuperaDel && (
                                                                    <div className="gp-storico-rec">recupera il passaggio del {formatData(r.recuperaDel)}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {r.stella ? (
                                                                    <span className="gp-storico-stella">
                                                                        <Stella livello={r.stella} volte={r.consecutive} />
                                                                        <span className="muted">×{r.consecutive}</span>
                                                                    </span>
                                                                ) : <span className="muted">—</span>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <div className="footer-hint">
                        1 mancata → stella gialla, {soglia} di fila → rossa; una tratta fatta azzera il conteggio.
                        I percorsi "in attesa" non contano finché non sono conclusi.
                    </div>
                    <div className="footer-actions">
                        <button className="btn btn-ghost" onClick={onClose}>Chiudi</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModaleStoricoTratta;
